import React, { useEffect, useRef } from 'react'
import { useMount } from 'react-use'

import { cn } from '@/lib/utils'

export interface OverlayProps {
  children: React.ReactNode
  variant: 'dark' | 'body-bg' | 'white'
  contentCentered?: boolean
  className?: string
  fullScreen?: boolean
}

const Overlay: React.FC<OverlayProps> = ({
  children,
  variant,
  contentCentered,
  className,
  fullScreen = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const backgroundColor =
    variant === 'dark'
      ? 'color-mix(in srgb, black 70%, transparent)'
      : variant === 'body-bg'
        ? 'color-mix(in srgb, var(--background) 50%, transparent)'
        : 'color-mix(in srgb, white 50%, transparent)'

  // Scroll the overlay card into view if it's not visible
  useMount(() => {
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, 200)
  })

  // Prevent scrolling on the body when the overlay is in full screen mode
  useEffect(() => {
    if (!fullScreen) {
      return
    }

    document.getElementById('root')?.classList.add('overflow-y-hidden')

    return () => {
      if (fullScreen) {
        document.getElementById('root')?.classList.remove('overflow-y-hidden')
      }
    }
  })

  return (
    <div
      ref={containerRef}
      style={{
        backdropFilter: 'blur(5px)',
        backgroundColor: backgroundColor,
      }}
      className={cn(
        'z-overlay absolute top-0 right-0 bottom-0 left-0 w-full p-4',
        contentCentered ? 'flex items-center justify-center' : '',
        className,
      )}
    >
      {children}
    </div>
  )
}

export default Overlay
